import React, { useState } from "react";
import { motion } from "framer-motion";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import emailjs from "@emailjs/browser";
import * as zod from "zod";
import { AiFillCheckCircle } from "react-icons/ai";
import { MdError } from "react-icons/md";

import { fadeIn } from "../variants";

const contactSchema = zod.object({
  name: zod.string().min(1, { message: "The name field is required" }),
  email: zod
    .string()
    .min(1, { message: "The email field is required" })
    .email("The email provided is invalid"),
  message: zod.string().min(1, { message: "The message field is required" }),
});

const Contact = () => {
  const [feedback, setFeedback] = useState(null);

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const handleFormSubmit = async ({ name, email, message }) => {
    const templateParams = {
      from_name: name,
      email,
      message,
    };

    const serviceId = "service_i05atkp";
    const templateId = "template_jqsffht";
    const publicKey = "EFCulXM_Vjxv5m0hi";

    try{
      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setFeedback({
        category: "Success",
        message: "Email sent successfully!",
      })
    } catch {
      setFeedback({
        category: "Error",
        message: "Unable to send the email, please try again.",
      })
    }

    reset();
  };

  const getErrorMessageByFieldName = (name) => {
    const objKeys = Object.keys(errors);

    if (!objKeys.length || !objKeys.includes(name)) return;

    return errors[name].message;
  };

  return (
    <section id="contact" className="section">
      <div className="container mx-auto">
        <div className="flex flex-col">
          <motion.h4
            variants={fadeIn("right", "0.3")}
            initial="hidden"
            whileInView={"show"}
            viewport={{ once: false, amount: 0.3 }}
            className="flex-1 flex justify-start items-center mb-10"
          >
            <span className="text-2xl uppercase  text-accent fornt-medium mb-2 tracking-wide">
              Reach me out
            </span>
          </motion.h4>
          <motion.form
            variants={fadeIn("left", "0.5")}
            initial="hidden"
            whileInView={"show"}
            viewport={{ once: false, amount: 0.3 }}
            className="flex-1 border rounded-2xl flex flex-col gap-y-6 pb-24 p-6 items-start"
            onSubmit={handleSubmit(handleFormSubmit)}
            noValidate
          >
            <div className="w-full">
              <input
                type="text"
                className="bg-transparent border-b py-3 outline-none w-full  px-2 border-2 border-solid border-[#a6c9f8] rounded-md placeholder:text-white focus:border-accent transition-all"
                placeholder="Name"
                {...register("name")}
              />
              <span className="text-[#e25858]">
                {getErrorMessageByFieldName("name")}
              </span>
            </div>

            <div className="w-full">
              <input
                type="email"
                className="bg-transparent border-b py-3 outline-none w-full  px-2 border-2 border-solid border-[#a6c9f8] rounded-md placeholder:text-white focus:border-accent transition-all"
                placeholder="Email"
                {...register("email")}
              />
              <span className="text-[#e25858]">
                {getErrorMessageByFieldName("email")}
              </span>
            </div>

            <div className="mb-12 w-full">
              <textarea
                className="bg-transparent py-12 outline-none  w-full px-2 border-2 border-solid border-[#a6c9f8] rounded-md placeholder:text-white focus:border-accent transition-all resize-none"
                placeholder="Message"
                {...register("message")}
              ></textarea>
              <span className="text-[#e25858]">
                {getErrorMessageByFieldName("message")}
              </span>
            </div>

            <div>
              <button
                className="btn btn-lg btn-disabled disabled:cursor-not-allowed disabled:opacity-50 mb-2"
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
              >
                Send
              </button>
              <p>
                {feedback && (
                  <motion.div
                    variants={fadeIn('left', '0.4')}
                    initial="hidden"
                    whileInView={'show'}
                    viewport={{ once: false, amount: 0.7 }}
                    className="flex gap-2 items-center"
                  >
                    {feedback.category === "Success" ? (
                      <AiFillCheckCircle color="#51CA73" />
                    ) : (
                      <MdError color="#e25858" />
                    )}
                    <span>{feedback.message}</span>
                  </motion.div>
                )}
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
